import { MovementPhase } from './movementData'
// Fuel node status types based on fuel percentage
export type FuelStatus = 'green' | 'amber' | 'red' | 'black'
// Interface for bulk fuel nodes
export interface BulkFuelNode {
  id: string
  name: string
  label: string
  coordinates: [number, number] // [latitude, longitude]
  description: string
  isAlternate: boolean // Whether this is an alternate node
}
// Interface for fuel distribution corridors
export interface FuelDistributionCorridor {
  id: string
  name: string
  coordinates: [number, number][] // Array of coordinate pairs forming a route
  description: string
  nodeId: string // ID of the fuel node this corridor is connected to
}
// Interface for daily fuel status
export interface DailyFuelStatus {
  day: number
  percentageOnHand: number
  status: FuelStatus
  notes: string
}
// Status colors for visual representation
export const fuelStatusColors: Record<FuelStatus, string> = {
  green: '#059669', // Green
  amber: '#D97706', // Amber
  red: '#DC2626', // Red
  black: '#1F2937', // Black
}
// Get fuel status based on percentage
export const getFuelStatus = (percentage: number): FuelStatus => {
  if (percentage >= 75) return 'green'
  if (percentage >= 50) return 'amber'
  if (percentage >= 25) return 'red'
  return 'black'
}
// Bulk fuel nodes data
export const bulkFuelNodes: BulkFuelNode[] = [
  {
    id: 'fort-stockton',
    name: 'F1 - Fort Stockton Bulk POL Point',
    label: 'F1',
    coordinates: [30.8941, -102.8793], // Fort Stockton
    description: 'Primary bulk fuel distribution point',
    isAlternate: false,
  },
  {
    id: 'alpine',
    name: 'F1 (Alt) - Alpine Bulk POL Point',
    label: 'F1-ALT',
    coordinates: [30.3584, -103.6614], // Alpine
    description: 'Alternate bulk fuel distribution point',
    isAlternate: true,
  },
]
// Fuel distribution corridors
export const fuelDistributionCorridors: FuelDistributionCorridor[] = [
  {
    id: 'fd1-stockton',
    name: 'FD1 - Fort Stockton to Del Rio',
    coordinates: [
      [30.8941, -102.8793], // Fort Stockton
      [30.1422, -102.3979], // Sanderson (intermediate point)
      [29.3627, -100.8968], // Del Rio
    ],
    description: 'Primary fuel distribution corridor',
    nodeId: 'fort-stockton',
  },
  {
    id: 'fd1-alpine',
    name: 'FD1 - Alpine to Del Rio',
    coordinates: [
      [30.3584, -103.6614], // Alpine
      [30.1422, -102.3979], // Sanderson (intermediate point)
      [29.3627, -100.8968], // Del Rio
    ],
    description: 'Alternate fuel distribution corridor',
    nodeId: 'alpine',
  },
]
// Daily fuel status data
export const dailyFuelStatus: DailyFuelStatus[] = [
  {
    day: 1,
    percentageOnHand: 84,
    status: 'green',
    notes: 'Initial fill level',
  },
  {
    day: 2,
    percentageOnHand: 68,
    status: 'amber',
    notes: 'Mid drawdown',
  },
  {
    day: 3,
    percentageOnHand: 51,
    status: 'amber',
    notes: 'Warning threshold approaching',
  },
  {
    day: 4,
    percentageOnHand: 35,
    status: 'red',
    notes: 'Low – resupply imminent',
  },
  {
    day: 5,
    percentageOnHand: 100,
    status: 'green',
    notes: 'Resupply complete',
  },
  {
    day: 6,
    percentageOnHand: 84,
    status: 'green',
    notes: 'Post-resupply draw begins again',
  },
  {
    day: 7,
    percentageOnHand: 68,
    status: 'amber',
    notes: 'End of mission phase – refuel needed',
  },
]
// Helper function to get fuel status for a specific day
export const getFuelStatusForDay = (day: number): DailyFuelStatus => {
  return (
    dailyFuelStatus.find((status) => status.day === day) || dailyFuelStatus[0]
  )
}
// Helper function to convert time point to day number
export const timePointToDay = (timePoint: string): number => {
  switch (timePoint) {
    case '24h':
      return 1
    case '48h':
      return 2
    case '72h':
      return 3
    case '96h':
      return 4
    case '5d':
      return 5
    case '6d':
      return 6
    case '7d':
      return 7
    default:
      return 0 // For "now", show day 0 (not started)
  }
}
